import IconButton from 'components/Luxkit/Button/IconButton'
import React, { MouseEvent, useCallback, useMemo, useRef } from 'react'
import BundleAndSaveDateSelectionTrigger from './BundleAndSaveDateSelectionTrigger'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LineArrowsSwapVerticalIcon from 'components/Luxkit/Icons/line/LineArrowsSwapVerticalIcon'
import LineArrowsSwapHorizontalIcon from 'components/Luxkit/Icons/line/LineArrowsSwapHorizontalIcon'
import Group from 'components/utils/Group'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageState, PaginatorState } from 'contexts/OfferPage/offerPageStateReducer'
import OfferBundleAndSaveCalendarDatePicker from 'components/OfferPage/Filters/BundleAndSaveCalendarSidebarPicker/OfferBundleAndSaveCalendarDatePicker'
import { isBundleAndSaveV2Enabled } from 'lib/offer/offerUtils'
import LinePlusIcon from 'components/Luxkit/Icons/line/LinePlusIcon'
import DropdownSheetBase from 'components/Luxkit/Dropdown/Sheet/DropdownSheetBase'
import styled from 'styled-components'

const ControlsGroup = styled(Group)`
  &.elevated {
    position: relative;
    z-index: 3;
  }
`

interface Props {
  bundledOffers: OfferPageState['bundledOffers']
  onClickLabel: string
  isDatePickerOpen: boolean
  currentOfferId?: string
  defaultDayPicker?: boolean
  defaultMonth?: string
  useRooms?: boolean
  paginator?: PaginatorState;
  isCalendarAlreadyOpen?: boolean;
}

function BundleAndSaveDateSelection(props: Props) {
  const {
    bundledOffers,
    isDatePickerOpen,
    onClickLabel,
    defaultDayPicker,
    defaultMonth,
    currentOfferId,
    useRooms,
    paginator,
    isCalendarAlreadyOpen,
  } = props
  const dispatch = useOfferPageDispatch()
  const handleClick = useCallback(
    (_: MouseEvent<HTMLButtonElement>, offerId?: string) => {
      if (offerId) {
        dispatch({
          type: OfferPageActions.setPaginatorCurrentOfferId,
          currentOfferId: offerId,
        })
      }

      if (paginator?.currentOfferId === offerId || !isDatePickerOpen) {
        if (isDatePickerOpen) {
          dispatch({ type: OfferPageActions.closeFilter })
        } else {
          if (!isCalendarAlreadyOpen && !isBundleAndSaveV2Enabled()) {
            dispatch({ type: OfferPageActions.toggleSubFilter, filter: 'duration' })
          }
          dispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
        }
      }
    },
    [dispatch, isDatePickerOpen, paginator, isCalendarAlreadyOpen],
  )

  const onReverseClick = useCallback(
    () => {
      dispatch({ type: OfferPageActions.openSwapModal })
    },
    [dispatch],
  )

  const [firstHotelOffer, secondHotelOffer] = useMemo(
    () => Object.values(bundledOffers ?? {}),
    [bundledOffers],
  )

  const dateSelectionTriggerRef = useRef<HTMLDivElement>(null)
  const filtersGroupRef = useRef<HTMLDivElement>(null)

  if (!bundledOffers) {
    return null
  }

  return (
    <>
      <ControlsGroup
        className={isDatePickerOpen ? 'elevated' : undefined}
        ref={filtersGroupRef}
        fullWidth
        direction="vertical"
        tabletDirection="horizontal"
        horizontalAlign="center"
        tabletHorizontalAlign="center"
        tabletVerticalAlign="center"
        gap={4}
        tabletGap={12}
      >
        <BundleAndSaveDateSelectionTrigger
          ref={dateSelectionTriggerRef}
          hotelNumber={1}
          onClick={handleClick}
          noSelections={!firstHotelOffer?.checkInDate && !secondHotelOffer?.checkInDate}
          bundleOffer={firstHotelOffer}
          onClickLabel={onClickLabel}
          isDatePickerOpen={isDatePickerOpen}
          isSelected={
            firstHotelOffer.offer?.id === currentOfferId
          }
        />
        {!isBundleAndSaveV2Enabled() && <IconButton
          onClick={onReverseClick}
          shape="circle"
          variant="dark"
          kind="secondary"
        >
          <CSSBreakpoint as={LineArrowsSwapVerticalIcon} max="mobile" />
          <CSSBreakpoint as={LineArrowsSwapHorizontalIcon} min="tablet" />
        </IconButton>}

        {isBundleAndSaveV2Enabled() && <LinePlusIcon size={24}/>}

        <BundleAndSaveDateSelectionTrigger
          hotelNumber={2}
          onClick={handleClick}
          noSelections={!firstHotelOffer?.checkInDate && !secondHotelOffer?.checkInDate}
          bundleOffer={secondHotelOffer}
          onClickLabel={onClickLabel}
          isDatePickerOpen={isDatePickerOpen}
          isSelected={
            secondHotelOffer.offer?.id === currentOfferId
          }
        />
      </ControlsGroup>
      <DropdownSheetBase
        size="L"
        height="max"
        anchorRef={filtersGroupRef}
        triggerRef={dateSelectionTriggerRef}
        open={isDatePickerOpen}
        data-testid="offer-checkin-select"
      >
        <OfferBundleAndSaveCalendarDatePicker
          defaultDayPicker={defaultDayPicker}
          defaultMonth={defaultMonth}
          useRooms={useRooms}
          isCalendarAlreadyOpen={isCalendarAlreadyOpen}
        />
      </DropdownSheetBase>
    </>
  )
}

export default withOfferPageState((state) => {
  return {
    bundledOffers: state.bundledOffers,
    paginator: state.paginator,
    isDatePickerOpen: state.activeFilter === 'checkin',
    currentOfferId: state.paginator?.currentOfferId,
  }
})(BundleAndSaveDateSelection)
